import { useState, type ReactNode } from 'react';
import { Button, Input } from 'equasion-lib';
import {
  manualPickVerifyUnit,
  type PickVerifyUnitInput
} from '@/lib/scanning/retailCode';
import { eqProps } from '../eqProps';
import type { VerifyScanDecision } from '../verifyScanQueue';

export type VerifyManualEntryKind = 'serialized' | 'code';

type VerifyManualEntryProps = {
  mode: 'add' | 'remove';
  /** The screen's triage of the entry (undefined once the screen stopped scanning). */
  onSubmit: (unit: PickVerifyUnitInput) => VerifyScanDecision | undefined;
  /** The screen's lot chooser for a typed code (`choose-lot`): shown in place of the form. */
  lotChooser?: ReactNode;
};

/** The line under the form after a submit: sent to the server, or held back. */
type EntryNote = { kind: 'sent' | 'held'; text: string };

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim()) && Number(value) >= 1;

/**
 * Manual entry for Verify, shown in SharedScanner's modal (`replaceManualEntry`) so the laser's
 * capture field gets focus back when it closes. Two forms:
 *  - Serialized — GTIN + serial: a unit whose 2D barcode will not read (what the camera would have
 *    sent, typed);
 *  - Product code (NDC / UPC / GTIN) + quantity: bulk / OTC products, several at once.
 * Every submit goes through the same triage and queue as a scan (duplicate serials and over-scans
 * are still caught on the spot, the server still refuses anything wrong). The modal stays open for the next entry —
 * SharedScanner owns its open state; the serial / quantity fields clear, the GTIN / code stays.
 * Only an entry the triage actually queued reads as "sent" and clears: one whose key is still in
 * flight (a double tap, or a second quantity of the same code before the first has saved) is
 * dropped by the triage's double-tap guard, so the form says so and keeps what was typed. A code
 * whose line can take units from more than one lot is handed to the screen's lot chooser, which
 * takes the form's place (with the typed quantity) until it is done or closed.
 */
function VerifyManualEntry({
  mode,
  onSubmit,
  lotChooser = null
}: VerifyManualEntryProps) {
  const [kind, setKind] = useState<VerifyManualEntryKind>('serialized');
  const [gtin, setGtin] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState<EntryNote | null>(null);

  const serializedReady = !!gtin.trim() && !!serialNumber.trim();
  const codeReady = !!code.trim() && isWholeNumber(quantity);
  const verb = mode === 'remove' ? 'Remove' : 'Add';

  /**
   * What the form says after a submit. Queued → "sent", and the per-unit field clears. Handed to
   * the lot chooser → the field clears too (the chooser carries the quantity on), with no line of
   * its own. Held back because the same entry is still saving → say so, keep the fields. Anything
   * else (a duplicate serial or an over-scan, already alerted by the screen; a paused or stopped
   * screen) → no "sent" line.
   */
  const settle = (
    decision: VerifyScanDecision | undefined,
    { sent, held, clear }: { sent: string; held: string; clear: () => void }
  ) => {
    if (decision?.type === 'enqueue') {
      setNote({ kind: 'sent', text: sent });
      clear();
    } else if (decision?.type === 'choose-lot') {
      setNote(null);
      clear();
    } else if (decision?.type === 'ignore' && decision.reason === 'in-flight') {
      setNote({ kind: 'held', text: held });
    } else {
      setNote(null);
    }
  };

  const submitSerialized = () => {
    if (!serializedReady) return;
    const serial = serialNumber.trim();
    settle(onSubmit(manualPickVerifyUnit({ gtin, serialNumber })), {
      sent: `Serial ${serial} sent.`,
      held: `Serial ${serial} is still saving — watch the list for the result.`,
      clear: () => setSerialNumber('')
    });
  };

  const submitCode = () => {
    if (!codeReady) return;
    const typedCode = code.trim();
    settle(onSubmit(manualPickVerifyUnit({ code, quantity })), {
      sent: `${quantity.trim()} × ${typedCode} sent.`,
      held: `Still saving the previous ${typedCode} entry — add again once it lands.`,
      clear: () => setQuantity('1')
    });
  };

  if (lotChooser) return <div className="verify-manual">{lotChooser}</div>;

  return (
    <div className="verify-manual">
      <strong>
        {mode === 'remove' ? 'Remove a unit from this order' : 'Enter a unit to verify'}
      </strong>
      <div className="verify-manual__kinds" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'serialized'}
          className={kind === 'serialized' ? 'active' : ''}
          onClick={() => setKind('serialized')}
        >
          Serialized — GTIN + serial
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'code'}
          className={kind === 'code' ? 'active' : ''}
          onClick={() => setKind('code')}
        >
          Product code + quantity
        </button>
      </div>

      {kind === 'serialized' ? (
        <>
          <Input
            {...eqProps({
              form: false,
              label: 'GTIN',
              value: gtin,
              onChange: ({ value }: { value: string }) => setGtin(value)
            })}
          />
          <Input
            {...eqProps({
              form: false,
              label: 'Serial Number',
              value: serialNumber,
              onChange: ({ value }: { value: string }) => setSerialNumber(value)
            })}
          />
          <Button
            type="primary"
            fullWidth
            disabled={!serializedReady}
            {...eqProps({ onClick: submitSerialized })}
          >
            {verb} serial
          </Button>
        </>
      ) : (
        <>
          <Input
            {...eqProps({
              form: false,
              label: 'NDC / UPC / GTIN',
              value: code,
              onChange: ({ value }: { value: string }) => setCode(value)
            })}
          />
          <Input
            {...eqProps({
              form: false,
              label: 'Quantity',
              value: quantity,
              onChange: ({ value }: { value: string }) => setQuantity(value)
            })}
          />
          {!!quantity.trim() && !isWholeNumber(quantity) && (
            <p className="verify-manual__error">
              Enter a whole number of units (1 or more).
            </p>
          )}
          <Button
            type="primary"
            fullWidth
            disabled={!codeReady}
            {...eqProps({ onClick: submitCode })}
          >
            {verb} {isWholeNumber(quantity) ? quantity.trim() : ''} unit(s)
          </Button>
        </>
      )}
      {note?.kind === 'sent' && (
        <p className="verify-manual__sent" role="status">
          {note.text} Watch the list for the result.
        </p>
      )}
      {note?.kind === 'held' && (
        <p className="verify-manual__sent verify-manual__sent--held" role="status">
          {note.text}
        </p>
      )}
    </div>
  );
}

export default VerifyManualEntry;
